/**
 * World Cup feature — external URLs (not bundled; loaded at runtime).
 * Flags: https://assets.zporti.com/assets/Flags/{code}.svg
 */

import { SPORT_DEFINITIONS } from '../sports/sports.constants';

import { WC_BRAND_DEFAULT } from './helpers/wcBrand.constants';

/**
 * Competition ID for the 2026 tournament. Seeded in the backend.
 * TODO(multi-tournament): Fetch dynamically when multiple competitions exist.
 */
export const WORLD_CUP_COMPETITION_ID = 'world-cup-2026';

/**
 * Static default brand export. Runtime copy uses `getWcBrand()` from
 * `helpers/wcBrand.helper` (`Platform.OS === 'ios'` → App Store branding).
 */
export const WC_BRAND = WC_BRAND_DEFAULT;

/** App Store Guideline 5.2.1 — native iOS welcome footer only. */
export const TOURNAMENT_IP_DISCLAIMER =
  'Zporti is not affiliated with, endorsed by, or sponsored by FIFA. Team names, flags, and match schedules are used for identification only. All trademarks belong to their respective owners.';

/** Shown when squad name is missing from API/cache (never use internal group id in UI). */
export const WORLD_CUP_GROUP_NAME_FALLBACK = 'Group';

/** Persisted welcome completion: `platformStorage` key = KEY_PREFIX + '.' + userId | 'anonymous' */
export const WORLD_CUP_WELCOME_STORAGE = {
  KEY_PREFIX: 'zporti.worldCupWelcome.v1',
  COMPLETED_VALUE: '1',
} as const;

/**
 * Football's predicted-score cap.
 *
 * Now derived from the football sport definition (specs/multi-sport-core) rather than written
 * here, so there is one cap per sport instead of a hand-mirrored number. This constant used to
 * carry a comment claiming it matched the server's `MAX_SCORE` while it was 10 and the server
 * allowed 99; the server now reads the same definition, and `src/sports/__tests__/sportsParity`
 * fails if the two drift again.
 *
 * The World Cup is a football competition, so this is its cap. Sport-agnostic surfaces should
 * read `getSportDefinition(sport).maxPredictedScore` instead of importing this.
 */
export const MAX_COMPETITION_PREDICTED_SCORE = SPORT_DEFINITIONS.FOOTBALL.maxPredictedScore;

/** Debounce before PUT /competitions/:id/picks/:matchId after last local edit */
export const WORLD_CUP_PICK_SAVE_DEBOUNCE_MS = 650;

/** Must match server `competitionGroup.constants.js` */
export const WORLD_CUP_PAYOUT_MODE = {
  WINNER_TAKE_ALL: 'WINNER_TAKE_ALL',
  TOP_THREE: 'TOP_THREE',
} as const;

export type WorldCupPayoutMode = (typeof WORLD_CUP_PAYOUT_MODE)[keyof typeof WORLD_CUP_PAYOUT_MODE];

/** Must match server `MAX_DESCRIPTION_LENGTH` for competition groups */
export const WORLD_CUP_GROUP_DESCRIPTION_MAX_LENGTH = 2000;

/** Must match server `MAX_ENTRY_FEE_COINS` in competitionGroup.constants.js */
export const WORLD_CUP_MAX_ENTRY_FEE_COINS = 10_000_000;

/**
 * Full-tab welcome flow: Splash → Rules → Point system → main app.
 * Shown once per user (storage); global menu “World Cup rules” opens the same flow until completed.
 */
export const WORLD_CUP_WELCOME = {
  SPLASH: {
    SUBTITLE: WC_BRAND_DEFAULT.NAME,
    TAGLINE: 'Predict scores. Climb the ranks. Glory awaits.',
    /**
     * 2026 co-hosts — same ISO codes as `CountryFlag` / flagcdn everywhere else in the feature.
     * (Emoji flags were inconsistent across platforms; text-only was a stopgap.)
     */
    HOST_NATIONS: [
      {
        COUNTRY_CODE: 'us',
        ACCESSIBILITY_LABEL: 'United States, 2026 host nation',
      },
      {
        COUNTRY_CODE: 'mx',
        ACCESSIBILITY_LABEL: 'Mexico, 2026 host nation',
      },
      {
        COUNTRY_CODE: 'ca',
        ACCESSIBILITY_LABEL: 'Canada, 2026 host nation',
      },
    ] as const,
    CTA: "Let's Go",
  },
  RULES: {
    LABEL: 'How it works',
    TITLE: 'Rules',
    SUBTITLE: 'Four things to know before you start calling matches.',
    ITEMS: [
      {
        title: 'Predict Every Match',
        desc: 'Submit your score predictions before kickoff. Once a match starts, predictions lock.',
      },
      {
        title: 'Scoring System',
        desc: 'Exact score = full points. Correct outcome = partial points. Points increase in knockout rounds.',
      },
      {
        title: 'Double Points',
        desc: 'You get a fresh ×2 every round (each Group Stage round 1, 2 and 3, plus the Round of 32). Use it to double one match that round. High risk, high reward.',
      },
      {
        title: 'Tournament Picks',
        desc: 'Choose your predicted winner and top scorer before the tournament starts for bonus points.',
      },
      {
        title: 'Groups & Leagues',
        desc: 'Create or join private groups. Compete against friends on a separate leaderboard.',
      },
    ] as const,
    CTA: 'See point system',
  },
  POINTS_TABLE: {
    LABEL: 'Scoring',
    TITLE: 'Point system',
    COL_ROUND: 'Round',
    COL_EXACT: 'Exact',
    COL_OUTCOME: 'Outcome',
    COL_GOLDEN_BALL: 'Double Points',
    ROWS: [
      { stage: 'Group Stage',    hit: 4,  result: 2,  goldenBall: true  },
      { stage: 'Round of 32',    hit: 5,  result: 3,  goldenBall: true  },
      { stage: 'Round of 16',    hit: 7,  result: 4,  goldenBall: false },
      { stage: 'Quarter-Finals', hit: 10, result: 6,  goldenBall: false },
      { stage: 'Semi-Finals',    hit: 14, result: 8,  goldenBall: false },
      { stage: 'Third Place',    hit: 15, result: 9,  goldenBall: false },
      { stage: 'Final',          hit: 20, result: 12, goldenBall: false },
    ] as const,
    DOUBLE_POINTS_TITLE: 'Double Points',
    DOUBLE_POINTS_BODY:
      'You get one ×2 per round, not one for the whole tournament: a separate ×2 in each Group Stage round (1, 2 and 3) and one in the Round of 32. Each one doubles a single match in that round, so use it every round.',
    TOURNAMENT_PICKS_TITLE: 'Tournament Picks',
    TOURNAMENT_PICKS_BODY:
      'Predict the winner (+15 pts) and top goal scorer (+15 pts) before the tournament begins.',
    PRO_TIPS_TITLE: 'Scoring tips',
  },
  /** Dismiss entire welcome flow (all phases) — tab route + menu overlay */
  SKIP: 'Skip',
  CTA_ENTER_APP: 'Start Predicting',
} as const;

export const FLAG_ASSETS_CDN = {
  BASE_URL: 'https://assets.zporti.com',
  FLAGS_PATH: '/assets/Flags',
  EXTENSION: 'svg',
} as const;

/** WorldCupStandingsModal — full-screen BaseModal over World Cup shell */
export const WORLD_CUP_STANDINGS_MODAL = {
  BASE_MODAL_BLUR_INTENSITY: 20,
} as const;
